import Vue from "vue";
import Vuex from "vuex";
import router from "@/router";
import AuthService from "@/services/authService";
import ODService from "@/services/odService";
import EventBus from "@/eventBus";
import plivoBrowserSdk from "@/plugins/plivo";
import { ALERT } from "@/constants/alert.js";

// import vuex modules
import { moduleHome } from "./modules/moduleHome";
import { moduleOSClaims } from "./modules/moduleOSClaims";
import { moduleRecall } from "./modules/moduleRecall";
import { moduleFrontDesk } from "./modules/moduleFrontDesk";
import { moduleVPI } from "./modules/moduleVPI";
import { modulePlivo } from "./modules/modulePlivo";
import { modulePeople } from "./modules/modulePeople";
import { moduleConvos } from "./modules/moduleConvos";
import { moduleTags } from "./modules/moduleTags";
import { moduleWindow } from "./modules/moduleWindow";
import { moduleOpenAI } from "./modules/moduleOpenAI";
import { moduleCalendar } from "./modules/moduleCalendar";
import { moduleODConnector } from './modules/moduleODConnector';
import { moduleLabCases } from './modules/moduleLabCases';
import  { modulethreadconvo } from './modules/modulethreadconvo';
import { moduleTaskEngine } from "./modules/moduleTaskEngine";
import { moduleVideo } from "./modules/moduleVideo";
import { moduleAIValidation } from "./modules/moduleAIValidation";
// import vues-extensions plugin (allows the state to be reset easily)
import { createStore } from "vuex-extensions";

// import vuex-persistedstate plugin
import createPersistedState from "vuex-persistedstate";
const dataState = createPersistedState({
  paths: ["user", "clientName", "clientId", "validUser", "clientTimeZone", "isODEnabled"],
  storage: window.sessionStorage,
});

// import custom socket plugin
import socketPlugin from "./socketPlugin";

const store = createStore(Vuex.Store, {
  modules: {
    home: moduleHome,
    osclaims: moduleOSClaims,
    recall: moduleRecall,
    frontdesk: moduleFrontDesk,
    vpi: moduleVPI,
    plivo: modulePlivo,
    people: modulePeople,
    convos: moduleConvos,
    tags : moduleTags,
    window: moduleWindow,
    openAI: moduleOpenAI,
    calendar : moduleCalendar,
    odconnector: moduleODConnector,
    labcases:moduleLabCases,
    threadconvo:modulethreadconvo,
    taskengine: moduleTaskEngine,
    video: moduleVideo,
    aiValidation: moduleAIValidation,

  },
  plugins: [dataState, socketPlugin()],
  state: {
    // information about the user that is logged in (null if not logged in)
    user: null,
    // the name of the office
    clientName: "",
    clientTimeZone : "",
    clientId: null,
    validUser: '',
    isPanelVisible:false,
    contactState:{},
    internalSelectedConvo:{},
    isODEnabled : '',
    emailFetchProgress: { progress: 0, status: '' }, // For email fetch progress of overall contacts
    emailFetchProgressContact: { progress: 0, status: '', import_id : null }, // For email fetch progress of imported contacts
  },
  getters: {
    getContactState(state){
      return state.contactState
    },
    emailFetchProgress(state) {
      return state.emailFetchProgress;
    },
    emailFetchProgressContact(state) {
      return state.emailFetchProgressContact;
    },
    clientName(state) {
      return state.clientName;
    },
    clientTimeZone(state){
      return state.clientTimeZone;
    },
    clientId(state) {
      return state.clientId;
    },
    userId(state) {
      return state.user ? state.user.user_id : "";
    },
    username(state) {
      return state.user ? state.user.username : "";
    },
    firstName(state) {
      return state.user ? state.user.first_name : "";
    },
    lastName(state) {
      return state.user ? state.user.last_name : "";
    },
    fullName(state, getters) {
      return (getters.firstName || "") + (getters.firstName ? " " : "") + (getters.lastName || "");
    },
    userRole(state) {
      return state.user ? state.user.role : "";
    },
    AImode(state) {
      return state.user ? state.user.AI_mode : false;
    },
    // Check if user is admin or superadmin (role can be a string or array)
    isAdmin(state){
      const role = state.user?.role;
      if (!role) return false;
      // Handle array of roles (from JWT payload)
      if (Array.isArray(role)) {
        return role.some(r => r?.toLowerCase() === 'admin' || r?.toLowerCase() === 'superadmin');
      }
      // Handle single role string
      return role.toLowerCase() === 'admin';
    },
    // Check if user is superadmin (client_id === 1)
    isSuperAdmin(state){
      const role = state.user?.role;
      if (!role) return false;
      if (Array.isArray(role)) {
        return role.some(r => r?.toLowerCase() === 'superadmin');
      }
      return false;
    },
    // Check if user is a Claims Specialist (role can be a string or array)
    isClaimsSpecialist(state){
      const role = state.user?.role;
      if (!role) return false;
      if (Array.isArray(role)) {
        return role.some(r => r?.toLowerCase() === 'claims specialist');
      }
      return role.toLowerCase() === 'claims specialist';
    },
    userInitials(state, getters) {
      return getters.username.substring(0, 2).toUpperCase();
    },
    plivoUser(state) {
      return state.user ? state.user.plivo_user : "";
    },
    plivoPass(state) {
      return state.user ? state.user.plivo_pass : "";
    },
    email(state) {
      return state.user ? state.user.email : "";
    },
    darkMode(state) {
      return state.user ? state.user.dark_mode : false;
    },
    loggedIn(state) {
      return state.user != null;
    },
    getContact(state){
      return state.contactState;
    },
    isODEnabled(state){
      return state.isODEnabled;
    }
  },
  // Mutations Must Be Synchronous!
  mutations: {
    setContact(state,newContact){
      state.contactState=newContact
    },
    user(state, newUser) {
      state.user = newUser;
    },
    clientName(state, name) {
      state.clientName = name;
    },
    clientTimeZone(state, value){
      state.clientTimeZone = value
    },
    clientId(state, id) {
      state.clientId = id;
    },
    changeDarkMode(state, dark_mode) {
      state.user.dark_mode = dark_mode;
    },
    loggedIn(state, status) {
      state.validUser = status
    },
    changeRightPanelStatus(state,status){
      state.isPanelVisible=status;
    },
    isODEnabled(state, value){
      state.isODEnabled = value;
    },
    setEmailFetchProgress(state, payload) {
      state.emailFetchProgress = payload;
    },
    setEmailFetchProgressContact(state, payload) {
      state.emailFetchProgressContact = payload;
    }
  },
  actions: {
    logIn({ commit, dispatch }, payload) {
      return new Promise((resolve, reject) => {
        if (payload.username === "" || payload.password === "" || payload.client_id === "") {
          return reject("All the fields must be filled in!");
        }

        AuthService.login({ username: payload.username, password: payload.password,client_id: payload.client_id })
          .then((response) => {
            if (response.data === "" || response.data.user.plivo_user === undefined) {
              return reject("Something went wrong");
            }
            var user = {
              user_id: response.data.user.user_id,
              username: response.data.user.username,
              plivo_user: response.data.user.plivo_user,
              plivo_pass: response.data.user.plivo_pass,
              first_name: response.data.user.first_name,
              last_name: response.data.user.last_name,
              role: response.data.user.role,
              email: response.data.user.email,
              dark_mode: response.data.user.dark_mode,
              AI_mode: response.data.user.client.AI_mode,
            };
            commit("user", user);
            commit("loggedIn", true);
            commit("clientName", response.data.user.client.name);
            commit("clientTimeZone", response.data.user.client.timezone)
            commit("clientId", payload.client_id);
            EventBus.$emit("toggleDarkMode", user.dark_mode);
            dispatch("checkIsODEnabled");
            dispatch("people/getUsers", { root: true });
            dispatch("people/getContacts", { root: true });
            const postLoginRedirect = sessionStorage.getItem('postLoginRedirect');
            if (postLoginRedirect) {
              sessionStorage.removeItem('postLoginRedirect');
              window.location.href = postLoginRedirect;
            } else {
              router.push({ path: "/frontdesk" });
            }
            dispatch("logInPlivoSDK");
            return resolve("successfully logged in");
          })
          .catch((e) => {
            console.log(e);
            EventBus.$emit("alert", {
              message: "Invalid username or password",
              type: ALERT.ERROR,
            });
            return reject("Username or password incorrect!");
          });
      });
    },
    // logs in the plivo browser sdk
    logInPlivoSDK({ getters }) {
      console.log(this)
      if (getters.loggedIn && !plivoBrowserSdk.client.isLoggedIn)
        plivoBrowserSdk.client.login(getters.plivoUser, getters.plivoPass);
    },
    async checkIsODEnabled({commit}){
      try {
        let res = await ODService.isODEnabled();
        commit("isODEnabled",res.data);
      } catch (error) {
        console.error(error);
        commit("isODEnabled",false);
      }
    },
    async logOut({ commit }) {
      await AuthService.logout().catch((e) => console.log(e));
      plivoBrowserSdk.client.logout();
      EventBus.$emit("toggleDarkMode", false);
      commit("user", null);
      commit("clientName", "");
      commit("clientId", null);
      commit("loggedIn", false);
      router.push({ path: "/" });
      // reset the store to the default configuration
      this.replaceState({
        home: {},
        osclaims: {},
        recall: {},
        frontdesk: {},
        vpi: {},
        plivo: {},
        people: {},
        convos: {},
        tags: {},
        calendar: {},
      });
      window.location.reload(true);
    },
  },
});

export default store;
